import { systemCalculationByRef } from './fieldCalculations';
import type {
  CalculationDefinition,
  CalculationFactorApplicability,
  QuoteFieldDefinitionRecord,
  QuoteFieldKind,
  QuoteFieldPlacement,
  QuoteFieldType,
} from './types';

export type UnifiedFieldObject =
  | 'device'
  | 'package'
  | 'plan'
  | 'customer'
  | 'quote-header'
  | 'quote-line';

export const FIELD_CENTER_OBJECTS: readonly UnifiedFieldObject[] = [
  'device',
  'package',
  'plan',
  'customer',
];

export interface UnifiedFieldBinding {
  owner: UnifiedFieldObject;
  storagePath: string;
  /** The canonical resolver is shared by pages, snapshots and projections. */
  valueResolver: string;
  writeCommand?: string;
  pageConsumer: string;
  /** Every concrete UI/output landing for the same stable identity. */
  placements: string[];
  /** Quote snapshot path is a historical fact, not a second definition. */
  snapshotPath?: string;
  permissionResource: string;
  viewAction: string;
  editAction?: string;
  exportAction: string;
  snapshotPolicy: 'live-master' | 'freeze-on-publication' | 'freeze-on-select';
  poolAllowed: boolean;
  /** Stable quote landing for a pool reference; labels are presentation only. */
  quotePlacement?: QuoteFieldPlacement;
  sourceKind: 'system-fixed' | 'system-calculated' | 'enterprise';
  calculationRef?: string;
  /** Explicit cardinality contract for structured-calculation factors. */
  factorApplicability: CalculationFactorApplicability;
}

export interface UnifiedFieldDescriptor {
  definition: QuoteFieldDefinitionRecord;
  owner: UnifiedFieldObject;
  binding: UnifiedFieldBinding;
}

interface SystemFieldSeed {
  key: string;
  label: string;
  type: QuoteFieldType;
  owner: UnifiedFieldObject;
  source: string;
  writeCommand?: string;
  pageConsumer: string;
  placements?: string[];
  snapshotPath?: string;
  defaultVisible?: boolean;
  poolAllowed?: boolean;
  calculationRef?: string;
  calculated?: boolean;
  readOnly?: boolean;
  factorApplicability?: CalculationFactorApplicability;
  note?: string;
}

const OWNER_TARGETS: Record<
  UnifiedFieldObject,
  Omit<
    UnifiedFieldBinding,
    | 'owner'
    | 'storagePath'
    | 'sourceKind'
    | 'snapshotPolicy'
    | 'poolAllowed'
    | 'calculationRef'
    | 'valueResolver'
    | 'placements'
    | 'factorApplicability'
    | 'snapshotPath'
  >
> = {
  device: {
    writeCommand: 'saveDeviceCatalog',
    pageConsumer: '设备管理',
    permissionResource: 'devices',
    viewAction: 'devices:catalog.read',
    editAction: 'devices:catalog.manage',
    exportAction: 'quotes:quote.export',
  },
  package: {
    writeCommand: 'savePackageCatalog',
    pageConsumer: '套餐管理',
    permissionResource: 'packages',
    viewAction: 'packages:solution.read',
    editAction: 'packages:solution.manage',
    exportAction: 'quotes:quote.export',
  },
  plan: {
    writeCommand: 'savePlanCatalog',
    pageConsumer: '方案管理',
    permissionResource: 'plans',
    viewAction: 'plans:solution.read',
    editAction: 'plans:solution.manage',
    exportAction: 'quotes:quote.export',
  },
  customer: {
    writeCommand: 'saveCustomerCatalog',
    pageConsumer: '客户管理',
    permissionResource: 'customers',
    viewAction: 'customers:customer.read',
    editAction: 'customers:customer.manage',
    exportAction: 'quotes:quote.export',
  },
  'quote-header': {
    writeCommand: 'updateQuote',
    pageConsumer: '报价抬头',
    permissionResource: 'quotes',
    viewAction: 'quotes:quote.read',
    editAction: 'quotes:quote.edit',
    exportAction: 'quotes:quote.export',
  },
  'quote-line': {
    writeCommand: 'updateQuoteItem',
    pageConsumer: '报价清单',
    permissionResource: 'quotes',
    viewAction: 'quotes:quote.read',
    editAction: 'quotes:quote.edit',
    exportAction: 'quotes:quote.export',
  },
};

const SYSTEM_FIELDS: readonly SystemFieldSeed[] = [
  // Device (18)
  {
    key: 'device_name',
    label: '设备名称',
    type: 'text',
    owner: 'device',
    source: 'DeviceCatalogRecord.name',
    writeCommand: 'saveDeviceCatalog',
    pageConsumer: '设备管理',
    defaultVisible: true,
  },
  {
    key: 'device_sku',
    label: 'SKU',
    type: 'code',
    owner: 'device',
    source: 'DeviceCatalogRecord.sku',
    writeCommand: 'saveDeviceCatalog',
    pageConsumer: '设备管理',
    defaultVisible: true,
  },
  {
    key: 'jky_code',
    label: '吉客云编号',
    type: 'code',
    owner: 'device',
    source: 'DeviceCatalogRecord.jkyCode',
    writeCommand: 'saveDeviceCatalog',
    pageConsumer: '设备管理',
  },
  {
    key: 'device_category',
    label: '设备分类',
    type: 'text',
    owner: 'device',
    source: 'DeviceCatalogRecord.category',
    writeCommand: 'saveDeviceCatalog',
    pageConsumer: '设备管理',
  },
  {
    key: 'device_attribute',
    label: '货品属性',
    type: 'single-select',
    owner: 'device',
    source: 'DeviceCatalogRecord.deviceAttribute',
    writeCommand: 'saveDeviceCatalog',
    pageConsumer: '设备管理',
  },
  {
    key: 'procurement_attribute',
    label: '采购属性',
    type: 'single-select',
    owner: 'device',
    source: 'DeviceCatalogRecord.procurementAttribute',
    writeCommand: 'saveDeviceCatalog',
    pageConsumer: '设备管理',
    note: '设备主数据采购状态；缺失历史事实不推断。',
  },
  {
    key: 'device_weight',
    label: '重量（kg）',
    type: 'number',
    owner: 'device',
    source: 'DeviceCatalogRecord.weight',
    writeCommand: 'saveDeviceCatalog',
    pageConsumer: '设备管理',
    note: '设备主数据实重（kg）；缺失历史事实不推断。',
  },
  {
    key: 'device_status',
    label: '状态',
    type: 'single-select',
    owner: 'device',
    source: 'DeviceCatalogRecord.status',
    writeCommand: 'saveDeviceCatalog',
    pageConsumer: '设备管理',
    note: '系统状态只读',
  },
  {
    key: 'device_internal_remark',
    label: '内部备注',
    type: 'text',
    owner: 'device',
    source: 'DeviceCatalogRecord.remark',
    writeCommand: 'saveDeviceCatalog',
    pageConsumer: '设备管理',
    note: '不进入报价字段池',
  },
  {
    key: 'rental_price',
    label: '租金',
    type: 'money',
    owner: 'device',
    source: 'DeviceCatalogRecord.rentalPrice',
    writeCommand: 'saveDeviceCatalog',
    pageConsumer: '设备管理',
    defaultVisible: true,
  },
  {
    key: 'tax_inclusive_plan_price',
    label: '最终买断价',
    type: 'money',
    owner: 'device',
    source: 'DeviceCatalogRecord.taxInclusivePlanPrice',
    writeCommand: 'saveDeviceCatalog',
    pageConsumer: '设备管理',
    defaultVisible: true,
    note: '设备最终买断价格；历史字段名保持 API 兼容',
  },
  {
    key: 'device_unit_price',
    label: '单价',
    type: 'money',
    owner: 'device',
    source: 'DeviceCatalogRecord.unitPrice',
    writeCommand: 'saveDeviceCatalog',
    pageConsumer: '设备管理',
    defaultVisible: true,
    note: '设备主数据人工价格；不读取 QuoteItem.unitPrice',
  },
  {
    key: 'device_compensation_price',
    label: '赔付价格',
    type: 'money',
    owner: 'device',
    source: 'DeviceCatalogRecord.compensationPrice',
    writeCommand: 'saveDeviceCatalog',
    pageConsumer: '设备管理',
    defaultVisible: true,
    note: '设备主数据人工赔付价格；不读取 QuoteItem.unitPrice',
  },
  {
    key: 'retail_price_tax13',
    label: '零售价（含13%专票增值税）',
    type: 'money',
    owner: 'device',
    source: 'DeviceCatalogRecord.retailPriceTax13',
    writeCommand: 'saveDeviceCatalog',
    pageConsumer: '设备管理',
  },
  {
    key: 'purchase_final_price_tax13',
    label: '购买最终价（含13%专票增值税）',
    type: 'money',
    owner: 'quote-line',
    source: 'quoteTotals.purchaseFinalPriceTax13',
    pageConsumer: '报价工作台',
    placements: ['报价工作台', '发布快照'],
    calculated: true,
    readOnly: true,
  },
  {
    key: 'purchase_final_price_ex_tax13',
    label: '购买最终价（不含13%专票增值税）',
    type: 'money',
    owner: 'quote-line',
    source: 'quoteTotals.purchaseFinalPriceExTax13',
    pageConsumer: '报价工作台',
    placements: ['报价工作台', '发布快照'],
    calculated: true,
    readOnly: true,
  },
  {
    key: 'dynamic_cost',
    label: '动态成本',
    type: 'money',
    owner: 'device',
    source: 'DeviceCatalogRecord.dynamicCost',
    writeCommand: 'saveDeviceCatalog',
    pageConsumer: '设备管理',
  },
  {
    key: 'device_image',
    label: '设备图片',
    type: 'text',
    owner: 'device',
    source: 'DeviceCatalogRecord.image',
    writeCommand: 'saveDeviceCatalog',
    pageConsumer: '设备管理',
    defaultVisible: true,
  },
  {
    key: 'replacement_device',
    label: '替代设备',
    type: 'text',
    owner: 'device',
    source: 'DeviceCatalogRecord.replacementIds',
    writeCommand: 'saveDeviceCatalog',
    pageConsumer: '设备管理',
  },
  {
    key: 'warehouse_stock',
    label: '分仓库存',
    type: 'text',
    owner: 'device',
    source: 'DeviceCatalogRecord.stockByWarehouse',
    pageConsumer: '库存管理',
  },
  // Package (9)
  {
    key: 'package_name',
    label: '套餐名称',
    type: 'text',
    owner: 'package',
    source: 'PackageCatalogRecord.name',
    writeCommand: 'savePackageCatalog',
    pageConsumer: '套餐管理',
  },
  {
    key: 'package_plan_ids',
    label: '关联方案',
    type: 'text',
    owner: 'package',
    source: 'PackageCatalogRecord.planIds',
    writeCommand: 'savePackageCatalog',
    pageConsumer: '套餐管理',
  },
  {
    key: 'package_image',
    label: '套餐图片',
    type: 'text',
    owner: 'package',
    source: 'PackageCatalogRecord.image',
    writeCommand: 'savePackageCatalog',
    pageConsumer: '套餐管理',
  },
  {
    key: 'package_light_image',
    label: '套餐灯光图片',
    type: 'text',
    owner: 'package',
    source: 'PackageCatalogRecord.lightImage',
    writeCommand: 'savePackageCatalog',
    pageConsumer: '套餐管理',
  },
  {
    key: 'package_product_links',
    label: '关联设备',
    type: 'text',
    owner: 'package',
    source: 'PackageCatalogRecord.productLinks',
    writeCommand: 'savePackageCatalog',
    pageConsumer: '套餐管理',
  },
  {
    key: 'package_relation_quantity',
    label: '套餐关系数量',
    type: 'number',
    owner: 'package',
    source: 'PackageProductLink.quantity',
    factorApplicability: 'relation-scalar',
    writeCommand: 'savePackageCatalog',
    pageConsumer: '套餐管理',
  },
  {
    key: 'package_item_price',
    label: '套餐单品价',
    type: 'money',
    owner: 'package',
    source: 'PackageProductLink.packageItemPrice',
    factorApplicability: 'relation-scalar',
    writeCommand: 'savePackageCatalog',
    pageConsumer: '套餐管理',
    placements: [
      '套餐管理（关系层人工维护）',
      '报价字段池',
      '报价行',
      '发布快照',
    ],
    defaultVisible: true,
    note: '设备目录人工维护价格，套餐关系读取精确设备修订版价格；报价只读取冻结快照。',
  },
  {
    key: 'package_item_monthly_subtotal',
    label: '套餐单品价小计',
    type: 'money',
    owner: 'package',
    source: 'PackageProductLink.packageItemPrice × PackageProductLink.quantity',
    pageConsumer: '套餐管理',
    placements: [
      '套餐管理（关系层只读小计）',
      '报价字段池',
      '报价行',
      '发布快照',
    ],
    calculationRef: 'package-item-monthly-subtotal-v1',
    factorApplicability: 'relation-scalar',
    note: '关系层只读：套餐价/月 × 数量。',
  },
  {
    key: 'package_discount_quote_tax13',
    label: '套餐最终折后价',
    type: 'money',
    owner: 'quote-line',
    source:
      'QuoteItem.packageDiscountedQuoteSnapshot -> QuoteItem.calculationSnapshots.package_discount_quote_tax13',
    pageConsumer: '报价工作台',
    placements: ['报价工作台', '报价汇总'],
    snapshotPath: 'QuoteItem.calculationSnapshots.package_discount_quote_tax13',
    calculationRef: 'package-discounted-quote-v2',
    factorApplicability: 'approved-aggregate-result',
    // Historical V1 aggregate only. New quote fields use the explicit unit and
    // subtotal identities below, so the old result cannot re-enter the pool.
    poolAllowed: false,
  },
  {
    key: 'package_discounted_unit_price',
    label: '套餐最终折后价',
    type: 'money',
    owner: 'quote-line',
    source: 'calculation:package-discounted-unit-price-v2',
    pageConsumer: '报价工作台',
    placements: ['报价工作台', '报价汇总'],
    calculationRef: 'package-discounted-unit-price-v2',
    factorApplicability: 'approved-aggregate-result',
  },
  {
    key: 'package_discounted_subtotal',
    label: '套餐最终折后价小计',
    type: 'money',
    owner: 'quote-line',
    source: 'calculation:package-discounted-subtotal-v2',
    pageConsumer: '报价工作台',
    placements: ['报价工作台', '报价汇总'],
    calculationRef: 'package-discounted-subtotal-v2',
    factorApplicability: 'approved-aggregate-result',
  },
  // Plan (7)
  {
    key: 'plan_name',
    label: '方案名称',
    type: 'text',
    owner: 'plan',
    source: 'PlanCatalogRecord.name',
    writeCommand: 'savePlanCatalog',
    pageConsumer: '方案管理',
  },
  {
    key: 'scheme_discount',
    label: '方案专属折扣',
    type: 'percent',
    owner: 'plan',
    source: 'PlanCatalogRecord.discount',
    writeCommand: 'savePlanCatalog',
    pageConsumer: '方案管理',
    defaultVisible: true,
  },
  {
    key: 'plan_image',
    label: '方案图片',
    type: 'text',
    owner: 'plan',
    source: 'PlanCatalogRecord.image',
    writeCommand: 'savePlanCatalog',
    pageConsumer: '方案管理',
  },
  {
    key: 'plan_cases',
    label: '方案案例',
    type: 'text',
    owner: 'plan',
    source: 'PlanCatalogRecord.cases',
    writeCommand: 'savePlanCatalog',
    pageConsumer: '方案管理',
  },
  {
    key: 'plan_package_ids',
    label: '关联套餐',
    type: 'text',
    owner: 'plan',
    source: 'PlanCatalogRecord.packageIds',
    pageConsumer: '方案管理',
  },
  {
    key: 'scheme_product_price',
    label: '方案价/月',
    type: 'money',
    owner: 'plan',
    source: 'PackageProductLink.packageItemPrice + PlanCatalogRecord.discount',
    pageConsumer: '方案管理',
    calculationRef: 'plan-product-price-v2',
    factorApplicability: 'relation-scalar',
  },
  {
    key: 'scheme_product_subtotal',
    label: '方案价小计/月',
    type: 'money',
    owner: 'plan',
    source:
      'PackageProductLink.packageItemPrice + PlanCatalogRecord.discount + PackageProductLink.quantity',
    pageConsumer: '方案管理、报价工作台',
    calculationRef: 'plan-product-subtotal-v2',
    factorApplicability: 'approved-aggregate-result',
    note: '设备关系层＝方案价/月×数量；套餐层/方案层＝Σ 下级方案价小计。',
  },
  // Customer (6)
  {
    key: 'customer_no',
    label: '客户编号',
    type: 'code',
    owner: 'customer',
    source: 'CustomerCatalogRecord.customerNo',
    writeCommand: 'saveCustomerCatalog',
    pageConsumer: '客户管理',
  },
  {
    key: 'customer_name',
    label: '客户名称',
    type: 'text',
    owner: 'customer',
    source: 'CustomerCatalogRecord.name',
    writeCommand: 'saveCustomerCatalog',
    pageConsumer: '客户管理',
    defaultVisible: true,
  },
  {
    key: 'customer_contact',
    label: '联系人',
    type: 'text',
    owner: 'customer',
    source: 'CustomerCatalogRecord.contact',
    writeCommand: 'saveCustomerCatalog',
    pageConsumer: '客户管理',
  },
  {
    key: 'customer_phone',
    label: '联系电话',
    type: 'text',
    owner: 'customer',
    source: 'CustomerCatalogRecord.phone',
    writeCommand: 'saveCustomerCatalog',
    pageConsumer: '客户管理',
  },
  {
    key: 'customer_manager',
    label: '客户经理',
    type: 'text',
    owner: 'customer',
    source: 'CustomerCatalogRecord.manager',
    writeCommand: 'saveCustomerCatalog',
    pageConsumer: '客户管理',
  },
  {
    key: 'customer_status',
    label: '状态',
    type: 'single-select',
    owner: 'customer',
    source: 'CustomerCatalogRecord.status',
    pageConsumer: '客户管理',
  },
  // Quote-line quantity is a fixed base identity, not a device master field.
  {
    key: 'quantity',
    label: '数量',
    type: 'number',
    owner: 'quote-line',
    source: 'QuoteItem.quantity',
    writeCommand: 'updateQuoteItem',
    pageConsumer: '报价清单',
    defaultVisible: true,
    poolAllowed: true,
  },
  {
    key: 'quote_remark',
    label: '备注',
    type: 'text',
    owner: 'quote-line',
    source: 'QuoteItem.remark',
    writeCommand: 'updateQuoteItem',
    pageConsumer: '报价工作台',
    poolAllowed: true,
  },
  {
    key: 'rental_subtotal',
    label: '小计租金（元/月）',
    type: 'money',
    owner: 'quote-line',
    source: 'calculation:quote-rental-subtotal-v1',
    pageConsumer: '报价清单',
    defaultVisible: true,
    poolAllowed: true,
    calculationRef: 'quote-rental-subtotal-v1',
  },
  {
    key: 'unit_price_subtotal',
    label: '小计单价/月',
    type: 'money',
    owner: 'quote-line',
    source: 'calculation:quote-unit-price-subtotal-v1',
    pageConsumer: '报价工作台',
    defaultVisible: true,
    poolAllowed: true,
    calculationRef: 'quote-unit-price-subtotal-v1',
  },
  {
    key: 'item_discount',
    label: '折扣',
    type: 'percent',
    owner: 'quote-line',
    source: 'QuoteItem.discount',
    writeCommand: 'updateQuoteItem',
    pageConsumer: '报价清单',
    defaultVisible: true,
    poolAllowed: true,
  },
  {
    key: 'product_final_discount_price',
    label: '产品最终折后价',
    type: 'money',
    owner: 'quote-line',
    source: 'calculation:final-discounted-relation-line-v2',
    pageConsumer: '报价清单',
    defaultVisible: true,
    poolAllowed: true,
    calculationRef: 'final-discounted-relation-line-v2',
  },
  {
    key: 'dynamic_cost_subtotal',
    label: '动态成本小计',
    type: 'money',
    owner: 'quote-line',
    source: 'calculation:dynamic-cost-subtotal-v1',
    pageConsumer: '报价清单',
    defaultVisible: true,
    poolAllowed: true,
    calculationRef: 'dynamic-cost-subtotal-v1',
  },
  // Fixed quote header (not a field-center custom owner)
  {
    key: 'quote_title',
    label: '报价标题',
    type: 'text',
    owner: 'quote-header',
    source: 'Quote.title',
    writeCommand: 'updateQuote',
    pageConsumer: '报价抬头',
    defaultVisible: true,
  },
  {
    key: 'valid_until',
    label: '有效期',
    type: 'date',
    owner: 'quote-header',
    source: 'Quote.validUntil',
    writeCommand: 'updateQuote',
    pageConsumer: '报价抬头',
    defaultVisible: true,
  },
  {
    key: 'business_mode',
    label: '业务模式',
    type: 'single-select',
    owner: 'quote-header',
    source: 'Quote.businessModeCode',
    writeCommand: 'updateQuote',
    pageConsumer: '报价抬头',
    defaultVisible: true,
  },
  {
    key: 'deposit',
    label: '押金',
    type: 'money',
    owner: 'quote-header',
    source: 'Quote.deposit',
    writeCommand: 'updateQuote',
    pageConsumer: '报价抬头',
    defaultVisible: true,
  },
  {
    key: 'overall_discount',
    label: '整单折扣',
    type: 'percent',
    owner: 'quote-header',
    source: 'Quote.overallDiscount',
    writeCommand: 'updateQuote',
    pageConsumer: '报价抬头',
    defaultVisible: true,
  },
  // Customer facts project into the fixed quote header through the same
  // customer identity; there are no duplicate quote-header definitions.
];

const SYSTEM_CALCULATION_BY_KEY: Record<string, string> = {
  rental_subtotal: 'quote-rental-subtotal-v1',
  unit_price_subtotal: 'quote-unit-price-subtotal-v1',
  package_item_monthly_subtotal: 'package-item-monthly-subtotal-v1',
  product_final_discount_price: 'final-discounted-relation-line-v2',
  scheme_product_price: 'plan-product-price-v2',
  scheme_product_subtotal: 'plan-product-subtotal-v2',
  package_discount_quote_tax13: 'package-discounted-quote-v2',
  package_discounted_unit_price: 'package-discounted-unit-price-v2',
  package_discounted_subtotal: 'package-discounted-subtotal-v2',
  dynamic_cost_subtotal: 'dynamic-cost-subtotal-v1',
};

/** Explicit pool policy.  It is data, not a note-string convention: the
 * source field may be referenced by the quote pool even when it is hidden by
 * default, while internal remarks and fixed quote headers never enter it. */
const SYSTEM_FIELD_POOL_ALLOWED = new Set([
  'device_image',
  'device_name',
  'device_sku',
  'jky_code',
  'rental_price',
  'tax_inclusive_plan_price',
  'device_unit_price',
  'device_untaxed_plan_price',
  'device_compensation_price',
  'procurement_attribute',
  'device_weight',
  'rental_subtotal',
  'unit_price_subtotal',
  'package_plan_ids',
  'package_light_image',
  'package_item_price',
  'scheme_discount',
  'scheme_product_price',
  'scheme_product_subtotal',
  'item_discount',
  'product_final_discount_price',
  'package_discounted_unit_price',
  'package_discounted_subtotal',
  'retail_price_tax13',
  'purchase_final_price_tax13',
  'purchase_final_price_ex_tax13',
  'dynamic_cost',
  'dynamic_cost_subtotal',
  'package_item_monthly_subtotal',
  'warehouse_stock',
  'replacement_device',
  'device_category',
  'device_attribute',
  'quote_remark',
]);

/** The three quote landing contracts are keyed by stable owner/field identity. */
export const QUOTE_POOL_PACKAGE_SUMMARY_KEYS = new Set([
  'package_discount_quote_tax13',
]);

export function quotePlacementForOwner(
  owner: UnifiedFieldObject,
  fieldKey?: string,
): QuoteFieldPlacement | undefined {
  if (owner === 'quote-header') return 'quote-header';
  if (owner === 'package' || owner === 'plan') {
    if (fieldKey?.startsWith('field.custom.')) return 'package-summary';
    return fieldKey && QUOTE_POOL_PACKAGE_SUMMARY_KEYS.has(fieldKey)
      ? 'package-summary'
      : 'quote-line';
  }
  if (owner === 'device' || owner === 'quote-line' || owner === 'customer')
    return owner === 'customer' ? 'quote-header' : 'quote-line';
  return undefined;
}

const QUOTE_HEADER_KEYS = new Set([
  'quote_title',
  'customer_name',
  'customer_contact',
  'customer_phone',
  'valid_until',
  'business_mode',
  'deposit',
  'overall_discount',
]);

function bindingPlacements(seed: SystemFieldSeed): string[] {
  const placements = new Set(seed.placements ?? [seed.pageConsumer]);
  if (seed.poolAllowed ?? SYSTEM_FIELD_POOL_ALLOWED.has(seed.key))
    placements.add('报价字段池');
  if (QUOTE_HEADER_KEYS.has(seed.key)) placements.add('报价抬头');
  if (seed.calculationRef || SYSTEM_CALCULATION_BY_KEY[seed.key])
    placements.add('发布快照');
  return [...placements];
}

function definitionForSystemSeed(
  seed: SystemFieldSeed,
  index: number,
): QuoteFieldDefinitionRecord {
  const calculationRef =
    seed.calculationRef ?? SYSTEM_CALCULATION_BY_KEY[seed.key];
  const calculation = calculationRef
    ? systemCalculationByRef(calculationRef)
    : undefined;
  const kind: QuoteFieldKind =
    calculation || seed.calculated ? 'system-calculated' : 'system-fixed';
  const options =
    seed.key === 'device_status' || seed.key === 'customer_status'
      ? [
          { value: 'active', label: '启用' },
          { value: 'inactive', label: '停用' },
        ]
      : seed.key === 'procurement_attribute'
        ? ['可以采购', '不可采购', '暂停采购'].map((value) => ({
            value,
            label: value,
          }))
        : undefined;
  return {
    key: seed.key,
    label: seed.label,
    type: seed.type,
    width: seed.label.length > 10 ? 'long' : 'normal',
    note: seed.note,
    groupKey: `system:${seed.owner}`,
    groupLabel: seed.owner,
    sortOrder: index + 1,
    enabled: true,
    defaultVisible: seed.defaultVisible ?? false,
    sourceKind: 'system',
    systemLocked: true,
    options,
    ...(calculation ? { calculation } : {}),
    governance: {
      fieldKey: seed.key,
      kind,
      classification: 'ordinary',
      required: false,
      lockedAttributes: ['fieldKey', 'owner', 'type', 'source', 'enabled'],
      definitionVersion: 'definition-v2',
      ...(calculation
        ? {
            formulaRef: calculation.calculationRef,
            formulaVersion: calculation.definitionVersion,
          }
        : {}),
    },
  };
}

function bindingForSeed(seed: SystemFieldSeed): UnifiedFieldBinding {
  const target = OWNER_TARGETS[seed.owner];
  const calculation =
    seed.calculationRef ?? SYSTEM_CALCULATION_BY_KEY[seed.key];
  const poolAllowed =
    seed.poolAllowed ?? SYSTEM_FIELD_POOL_ALLOWED.has(seed.key);
  return {
    owner: seed.owner,
    storagePath: seed.source,
    valueResolver: 'resolveUnifiedFieldValue',
    ...(seed.readOnly
      ? {}
      : { writeCommand: seed.writeCommand ?? target.writeCommand }),
    pageConsumer: seed.pageConsumer,
    placements: bindingPlacements(seed),
    snapshotPath:
      seed.snapshotPath ??
      (seed.owner === 'device'
        ? `QuoteItem.deviceFieldSnapshot.${seed.key}`
        : QUOTE_HEADER_KEYS.has(seed.key)
          ? `Quote.customer.${seed.key.replace('customer_', '')}`
          : seed.calculationRef
            ? `QuoteColumnSnapshot.${seed.key}`
            : undefined),
    permissionResource: target.permissionResource,
    viewAction: target.viewAction,
    ...(seed.readOnly ? {} : { editAction: target.editAction }),
    exportAction: target.exportAction,
    snapshotPolicy:
      seed.owner === 'quote-line' || calculation || seed.calculated
        ? 'freeze-on-publication'
        : 'live-master',
    poolAllowed,
    ...(poolAllowed
      ? { quotePlacement: quotePlacementForOwner(seed.owner, seed.key) }
      : {}),
    sourceKind:
      calculation || seed.calculated ? 'system-calculated' : 'system-fixed',
    factorApplicability:
      seed.factorApplicability ??
      (calculation ? 'approved-aggregate-result' : 'entity-scalar'),
    ...(calculation ? { calculationRef: calculation } : {}),
  };
}

export function systemFieldSeeds(): readonly SystemFieldSeed[] {
  return SYSTEM_FIELDS;
}

export function createSystemFieldRegistry(): UnifiedFieldDescriptor[] {
  const descriptors: UnifiedFieldDescriptor[] = [];
  const byKey = new Map<string, UnifiedFieldDescriptor>();
  for (const [index, seed] of SYSTEM_FIELDS.entries()) {
    const existing = byKey.get(seed.key);
    if (!existing) {
      const descriptor = {
        definition: definitionForSystemSeed(seed, index),
        owner: seed.owner,
        binding: bindingForSeed(seed),
      };
      descriptors.push(descriptor);
      byKey.set(seed.key, descriptor);
      continue;
    }
    // A duplicate seed is only valid when it describes an additional landing
    // of the same identity (customer master -> quote header snapshot). Any
    // other collision is a registry error, never silently dropped.
    if (
      !(
        ['customer_name', 'customer_contact', 'customer_phone'].includes(
          seed.key,
        ) &&
        existing.owner === 'customer' &&
        seed.owner === 'quote-header'
      )
    ) {
      throw new Error(`统一字段注册表身份碰撞：${seed.key}`);
    }
    const additional = bindingForSeed(seed);
    existing.binding = {
      ...existing.binding,
      placements: [
        ...new Set([...existing.binding.placements, ...additional.placements]),
      ],
      snapshotPath: additional.snapshotPath ?? existing.binding.snapshotPath,
    };
  }
  return descriptors;
}

export function createUnifiedFieldRegistry(
  quoteDefinitions: readonly QuoteFieldDefinitionRecord[],
): UnifiedFieldDescriptor[] {
  const system = createSystemFieldRegistry();
  const byKey = new Map(
    system.map((descriptor) => [descriptor.definition.key, descriptor]),
  );
  const persistedKeys = new Set<string>();
  for (const definition of quoteDefinitions) {
    if (!definition.key || persistedKeys.has(definition.key)) {
      throw new Error(`统一字段定义身份重复：${definition.key || 'empty'}`);
    }
    persistedKeys.add(definition.key);
    if (definition.governance.fieldKey !== definition.key) {
      throw new Error(`统一字段治理身份不一致：${definition.key}`);
    }
    const rawOwner = definition.groupKey.replace(/^business:/, '');
    const existing = byKey.get(definition.key);
    const isCustom = definition.governance.kind === 'custom';
    if (existing && isCustom) {
      throw new Error(`统一字段注册表身份碰撞：${definition.key}`);
    }
    if (existing) {
      if (definition.sourceKind !== 'system' || !definition.systemLocked) {
        throw new Error(`系统字段治理属性无效：${definition.key}`);
      }
      // Keep the persisted quote definition metadata, while retaining the
      // canonical object binding and owner from the registry.
      byKey.set(definition.key, {
        ...existing,
        definition: {
          ...existing.definition,
          ...definition,
          calculation:
            definition.calculation ?? existing.definition.calculation,
        },
      });
      continue;
    }
    if (!isCustom) {
      throw new Error(`未注册的系统字段身份：${definition.key}`);
    }
    if (
      !['device', 'package', 'plan', 'customer', 'quote-line'].includes(
        rawOwner,
      )
    ) {
      throw new Error(`统一字段归属无效：${definition.key}`);
    }
    const owner = rawOwner as UnifiedFieldObject;
    const target = OWNER_TARGETS[owner] ?? OWNER_TARGETS['quote-line'];
    const poolAllowed = owner !== 'quote-header';
    byKey.set(definition.key, {
      definition,
      owner,
      binding: {
        owner,
        storagePath: isCustom
          ? owner === 'quote-line'
            ? `QuoteItem.manualFieldValues.${definition.key}`
            : `${owner === 'device' ? 'DeviceCatalogRecord' : owner === 'package' ? 'PackageCatalogRecord' : owner === 'plan' ? 'PlanCatalogRecord' : 'CustomerCatalogRecord'}.customFieldValues.${definition.key}`
          : `definition:${definition.key}`,
        valueResolver: 'resolveUnifiedFieldValue',
        writeCommand: isCustom ? 'updateCustomFieldValue' : target.writeCommand,
        pageConsumer: isCustom ? `${owner} 管理页面` : target.pageConsumer,
        placements: isCustom
          ? [
              `${owner}列表`,
              `${owner}详情`,
              `${owner}新增`,
              `${owner}编辑`,
              ...(poolAllowed ? ['报价字段池'] : []),
              ...(owner === 'quote-line' ? ['报价行', '发布快照'] : []),
            ]
          : [target.pageConsumer],
        snapshotPath:
          owner === 'quote-line'
            ? `QuoteItem.manualFieldValues.${definition.key}`
            : `${owner}.customFieldValues.${definition.key}`,
        permissionResource: target.permissionResource,
        viewAction: target.viewAction,
        editAction: target.editAction,
        exportAction: target.exportAction,
        snapshotPolicy:
          owner === 'quote-line'
            ? 'freeze-on-publication'
            : poolAllowed
              ? 'freeze-on-select'
              : 'live-master',
        poolAllowed,
        ...(poolAllowed
          ? {
              quotePlacement:
                owner === 'quote-line'
                  ? (definition.quotePlacement ?? 'quote-line')
                  : quotePlacementForOwner(owner, definition.key),
            }
          : {}),
        sourceKind: isCustom ? 'enterprise' : 'system-fixed',
        factorApplicability:
          owner === 'quote-line' ? 'relation-scalar' : 'entity-scalar',
        ...(definition.calculation
          ? { calculationRef: definition.calculation.calculationRef }
          : {}),
      },
    });
  }
  return [...byKey.values()].sort(
    (a, b) =>
      a.definition.sortOrder - b.definition.sortOrder ||
      a.definition.key.localeCompare(b.definition.key),
  );
}

export function unifiedFieldsForOwner(
  quoteDefinitions: readonly QuoteFieldDefinitionRecord[],
  owner: UnifiedFieldObject,
): UnifiedFieldDescriptor[] {
  return createUnifiedFieldRegistry(quoteDefinitions).filter(
    (descriptor) => descriptor.owner === owner,
  );
}

export function unifiedFieldBindingForKey(
  quoteDefinitions: readonly QuoteFieldDefinitionRecord[],
  key: string,
): UnifiedFieldBinding | undefined {
  return createUnifiedFieldRegistry(quoteDefinitions).find(
    (descriptor) => descriptor.definition.key === key,
  )?.binding;
}

export const FIXED_QUOTE_HEADER_FIELD_KEYS = [
  'quote_title',
  'customer_name',
  'customer_contact',
  'customer_phone',
  'valid_until',
  'business_mode',
  'deposit',
  'overall_discount',
] as const;

export const QUOTE_BASE_FIELD_KEYS = [
  'device_image',
  'device_name',
  'device_sku',
  'quantity',
] as const;

/** Mechanical binding matrix used by tests and governance diagnostics. */
export function unifiedFieldBindingMatrix(
  quoteDefinitions: readonly QuoteFieldDefinitionRecord[] = [],
) {
  const registry = createUnifiedFieldRegistry(quoteDefinitions);
  return registry.map(({ definition, owner, binding }) => ({
    key: definition.key,
    owner,
    label: definition.label,
    type: definition.type,
    sourceKind: definition.sourceKind,
    storagePath: binding.storagePath,
    writeCommand: binding.writeCommand,
    resolver: binding.valueResolver,
    placements: [...binding.placements],
    permissionResource: binding.permissionResource,
    snapshotPolicy: binding.snapshotPolicy,
    poolAllowed: binding.poolAllowed,
    quotePlacement: binding.quotePlacement,
    calculationRef:
      binding.calculationRef ?? definition.calculation?.calculationRef,
    factorApplicability: binding.factorApplicability,
  }));
}

export function fixedQuoteHeaderBindings(
  quoteDefinitions: readonly QuoteFieldDefinitionRecord[] = [],
) {
  const registry = createUnifiedFieldRegistry(quoteDefinitions);
  return FIXED_QUOTE_HEADER_FIELD_KEYS.map((key) => {
    const descriptor = registry.find((item) => item.definition.key === key);
    if (!descriptor) throw new Error(`固定报价抬头字段缺失：${key}`);
    return descriptor;
  });
}

export function isFieldCenterOwner(
  owner: string,
): owner is Exclude<UnifiedFieldObject, 'quote-header' | 'quote-line'> {
  return (FIELD_CENTER_OBJECTS as readonly string[]).includes(owner);
}

export function calculationForDefinition(
  definition: QuoteFieldDefinitionRecord,
): CalculationDefinition | undefined {
  return definition.calculation;
}
